import React, {ReactNode} from 'react';

import {InaccessibleInfo} from 'auth/RequestAccess';
import {getModelManager, ManagedModelTypes} from 'models/ManagedModelDefinitions';
import {forceFetchManagedModel, requestFetchManagedModel} from 'models/ManagedModelUtil';
import {InaccessibleModelSelector} from 'models/reduceInaccessibleModels';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import componentName from 'util/componentName';
import ErrorSubclass from 'util/ErrorSubclass';
import throttle from 'util/throttle';

import StoreWrapper, {Unsubscribe} from './StoreWrapper';

export const UPDATE_THROTTLE_MS = 1;

/**
 * Exception-based way to indicate that we're not done fetching data and should render a loading
 * state instead of the normal component. This should only be thrown from within enhanceProps, which
 * is always called in a try/catch to handle this case.
 */
class DataStillLoadingException extends ErrorSubclass {}

/**
 * Connector class that behaves like redux's connect, but allows listening to any number of flux
 * stores as well.
 *
 * An instance of DataConnector exists for the lifecycle of the connected component.
 */
export default abstract class DataConnector<
  Props extends {},
  EnhancedProps extends {} | null,
> extends StoreWrapper {
  // Subclasses should override stores and shouldSubscribeToRedux (provided by StoreWrapper) to indicate which
  // stores to subscribe to.
  props: Props;

  constructor(props: Props) {
    super();
    this.props = props;
  }

  static connect<Props extends {}, EnhancedProps extends {} | null>(
    this: ConcreteConnectorClass<Props, EnhancedProps>,
    WrappedComponent: React.ComponentType<Props & EnhancedProps>,
    options?: Partial<ConnectorOptions<DataConnectorState<EnhancedProps>>>
  ): React.ComponentClass<Props & RefProp> {
    return connectDataConnector(this, WrappedComponent, {
      throttleUpdates: false,
      shouldPropagateNextState: (state, nextState) => state !== nextState,
      ...options,
    });
  }

  /**
   * Subclasses should implement this method to determine which props to add to the props of the
   * wrapped component. Props and data dependencies are provided as class fields and methods rather
   * than arguments to this function.
   */
  abstract enhanceProps(): EnhancedProps;

  /**
   * Subclasses can override this method to execute a force-fetch of data every time the component mounts.
   *
   * Force-fetching means re-fetching the latest version of the data from the server, even if it's already
   * present in the store.
   *
   * NOTE: Nothing about how DataConnector calls this function guarantees that it will
   * do a force-fetch (or even that it will fetch data at all); it just guarantees that forceFetchOnMount
   * will be called at an appropriate time for force-fetching to occur.
   */
  forceFetchOnMount(): void {}

  /**
   * Render a loading state, called when DataStillLoadingException is thrown.
   *
   * There's a simple default loading state, or subclasses can override to define their own loading
   * state.
   */
  renderLoading(): ReactNode {
    return <CenteredHorizontalThrobber />;
  }

  fallBackToLoading(): never {
    throw new DataStillLoadingException();
  }

  /**
   * Get this managed model from the store, or, if it's not available, fall back to a loading state via an
   * exception and fetch it using the autobatcher.
   *
   * This call won't proceed until the model is fully fetched, so don't call this in a loop or else you'll end
   * up making O(n) sequential network requests! To fetch multiple models at once, use one of the "optional"
   * or "multiple" methods.
   */
  getOrFetchRequired<T extends keyof ManagedModelTypes>(modelType: T, key: string): ManagedModelTypes[T] {
    const model = getModelManager(modelType).getModel(key);
    if (model) {
      return model;
    } else {
      requestFetchManagedModel(modelType, key);
      return this.fallBackToLoading();
    }
  }

  /**
   * For each key, get the model or whether it's inaccessible from the store. For any keys that are missing
   * from the store, fetch using the autobatcher. If data is still loading, throw DataStillLoadingException.
   * Otherwise, return a mapping of each key to its model or its inaccessible info.
   */
  getOrFetchMultipleMaybeInaccessibleRequired<T extends keyof ManagedModelTypes>(
    modelType: T,
    keys: Array<string>
  ): {[key: string]: ManagedModelTypes[T] | InaccessibleInfo} {
    const modelsDict: {[key: string]: ManagedModelTypes[T] | InaccessibleInfo} = {};
    let fetching = false;
    for (const key of keys) {
      const model = getModelManager(modelType).getModel(key);
      const isInaccessible = getModelManager(modelType).isModelInaccessible(key);
      // It is unfortunately possible for an item to both be in the store _and_ be inaccessible. We should
      // only return the model if it's not inaccessible
      if (model && !isInaccessible) {
        modelsDict[key] = model;
        continue;
      }

      const inaccessibleSelector = new InaccessibleModelSelector(this.reduxState());
      const json = inaccessibleSelector.getInaccessibleInfo(modelType, key);
      if (json != null) {
        modelsDict[key] = json;
        continue;
      }

      // Inaccessible state is not entirely consolidated in InaccessibleModelSelector; isModelInaccessible is
      // overridden for several types to reference something else, e.g. inaccessibleFileIds
      // If we know an item is inaccessible, construct & return an InaccessibleInfo here to avoid an infinite
      // loop attempting to load the model
      if (isInaccessible) {
        modelsDict[key] = {invalidId: key};
        continue;
      }

      fetching = true;
      requestFetchManagedModel(modelType, key);
    }
    if (fetching) {
      throw new DataStillLoadingException();
    } else {
      return modelsDict;
    }
  }

  /**
   * Get this managed model from the store. If it's not available, check if model was found to be
   * inaccessible, and if so return the original inaccessible error info. Otherwise fall back to a loading
   * state and fetch it using the autobatcher.
   */
  getOrFetchMaybeInaccessibleRequired<T extends keyof ManagedModelTypes>(
    modelType: T,
    key: string
  ): ManagedModelTypes[T] | InaccessibleInfo {
    const modelsDict = this.getOrFetchMultipleMaybeInaccessibleRequired(modelType, [key]);
    return modelsDict[key];
  }

  /**
   * Get this managed model from the store. If not present, fetch using autobatcher and return null.
   */
  getOrFetchOptional<T extends keyof ManagedModelTypes>(
    modelType: T,
    key: string
  ): ManagedModelTypes[T] | null {
    const model = getModelManager(modelType).getModel(key);
    if (!model) {
      requestFetchManagedModel(modelType, key);
    }
    return model;
  }

  /**
   * Fetch multiple model instances of the same type at once. If not all of the model instances are fully
   * loaded yet, fall back to a loading state via an exception.
   */
  getOrFetchMultipleRequired<T extends keyof ManagedModelTypes>(
    modelType: T,
    keys: Array<string>
  ): {[key: string]: ManagedModelTypes[T]} {
    const modelsDict: {[key: string]: ManagedModelTypes[T]} = {};
    let fetching = false;
    for (const key of keys) {
      const model = getModelManager(modelType).getModel(key);
      if (model) {
        modelsDict[key] = model;
      } else {
        fetching = true;
        requestFetchManagedModel(modelType, key);
      }
    }
    if (fetching) {
      throw new DataStillLoadingException();
    } else {
      return modelsDict;
    }
  }

  /**
   * Fetch multiple model instances of the same type at once. If not all of the model instances are fully
   * loaded yet, return null and fetch the not-yet-loaded ones.
   *
   * Use this if some UI can be reasonably rendered without this data, or to make multiple bulk fetch requests
   * in the same DataConnector.
   */
  getOrFetchMultipleOptionalFull<T extends keyof ManagedModelTypes>(
    modelType: T,
    keys: Array<string>
  ): {[key: string]: ManagedModelTypes[T]} | null {
    const modelByKey = this.getOrFetchMultipleOptionalPartial(modelType, keys);
    if (Object.keys(modelByKey).length !== new Set(keys).size) {
      return null;
    }
    return modelByKey;
  }

  /**
   * Fetch multiple model instances of the same type at once. If not all of the model instances are fully
   * loaded yet, return an object containing just the ones that are loaded and fetch the rest.
   *
   * This is an advanced access pattern that should be used with care, for when you're able to show a partial
   * UI with some data from cache but other data missing. In most cases, getOrFetchMultipleOptionalFull or
   * getOrFetchMultipleRequired is best.
   */
  getOrFetchMultipleOptionalPartial<T extends keyof ManagedModelTypes>(
    modelType: T,
    keys: Array<string>
  ): {[key: string]: ManagedModelTypes[T]} {
    const modelByKey: {[key: string]: ManagedModelTypes[T]} = {};
    for (const key of keys) {
      const model = this.getOrFetchOptional(modelType, key);
      if (model) {
        modelByKey[key] = model;
      }
    }
    return modelByKey;
  }

  forceFetchMultiple<T extends keyof ManagedModelTypes>(modelType: T, keys: Array<string>): void {
    for (const key of keys) {
      forceFetchManagedModel(modelType, key);
    }
  }

  /**
   * This method is incorrectly typed and has unintuitive behavior, so don't use it for new code and migrate
   * existing usages to getOrFetchMultipleOptionalFull (preferred) or getOrFetchMultipleOptionalPartial.
   *
   * If allOrNothing if specified, then if any model is not yet loaded, the return value is the empty object.
   * You can use _.isEmpty to check for this case, but a more natural and typesafe alternative is to call
   * getOrFetchMultipleOptionalFull, which returns null in that case.
   *
   * If allOrNothing is not specified, then the return value is an object with exactly keys.length entries,
   * where the value is either the loaded model or null. That means that the value type is actually
   * "ManagedModelTypes[T] | null", so THE TYPE SIGNATURE IS INCORRECT. Object.values(result) will contain
   * nulls that must be explicitly handled but won't be caught by the type system. The type is kept as-is for
   * legacy reasons, but invocations should be moved to getOrFetchMultipleOptionalPartial, which gives a
   * sparse object in which all present values are non-null.
   *
   * @deprecated
   */
  getOrFetchMultipleOptionalDeprecated<T extends keyof ManagedModelTypes>(
    modelType: T,
    keys: Array<string>,
    allOrNothing: boolean = false
  ): {[key: string]: ManagedModelTypes[T] /* This is incorrect! See the docstring. */} {
    const modelByKey: {[key: string]: ManagedModelTypes[T]} = {};
    for (const key of keys) {
      // @ts-expect-error: As documented, this is incorrect and this method should no longer be used.
      modelByKey[key] = this.getOrFetchOptional(modelType, key);
    }
    if (allOrNothing && Object.values(modelByKey).some((model) => !model)) {
      return {};
    }
    return modelByKey;
  }
}

interface ConcreteConnectorClass<Props extends {}, EnhancedProps extends {} | null> {
  new (props: Props): DataConnector<Props, EnhancedProps>;
}

interface DataConnectorState<P> {
  isLoading: boolean;
  additionalProps: P | null;
}

interface ConnectorOptions<S> {
  // While the page is loading, `handleStoreChange` is often called many times in a row. The stores are
  // updated repeatedly — often with the same data. This can lead to slow page-load times.
  //
  // If `throttleUpdates` is enabled, DataConnector will only apply store changes once per UPDATE_THROTTLE_MS.
  throttleUpdates: boolean;

  // When `handleStoreChange` is called, we check if the current store is different from the updated one.
  // By default, we use referential equality to do this comparison. If the wrapped component is sensitive
  // to re-renders, it should provide a custom implementation of `shouldPropagateNextState`.
  //
  // e.g. `shouldPropagateNextState: (a, b) => !isEqual(a, b)`
  // - use `isEqual` from `react-fast-compare`
  shouldPropagateNextState: (state: S, nextState: S) => boolean;
}

export interface RefProp {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setRef?: (element: any) => void;
}

function connectDataConnector<Props extends {}, EnhancedProps extends {} | null>(
  DataConnectorClass: ConcreteConnectorClass<Props, EnhancedProps>,
  WrappedComponent: React.ComponentType<Props & EnhancedProps>,
  options: ConnectorOptions<DataConnectorState<EnhancedProps>>
): React.ComponentClass<Props & RefProp> {
  type State = DataConnectorState<EnhancedProps>;
  type Options = ConnectorOptions<DataConnectorState<EnhancedProps>>;

  class ConnectedWrappedComponent extends React.Component<Props & RefProp, State> {
    _isMounted: boolean = false;
    _connector: DataConnector<Props, EnhancedProps>;
    _unsubscribe: Unsubscribe;
    _options: Options;

    // It's not uncommon for an action to dispatch multiple events. Throttling allows us to avoid re-rendering
    // multiple times in response to the same action.
    _throttledMaybeUpdateState: () => void;

    static displayName = `${DataConnectorClass.name || 'DataConnector'}(${componentName(WrappedComponent)})`;

    constructor(props: Props) {
      super(props);
      this._options = options;
      this._connector = new DataConnectorClass(props);
      // We call forceFetchOnMount for the first time before the call to computeState in order to
      // avoid double-fetching the data that is force-fetched
      this._connector.forceFetchOnMount();
      this.state = this.computeState(props);
      this._unsubscribe = this._connector.subscribe(this._handleStoreChange);
      this._isMounted = true;

      this._throttledMaybeUpdateState = throttle(() => {
        this.maybeUpdateState(this.props);
      }, UPDATE_THROTTLE_MS);
    }

    override UNSAFE_componentWillReceiveProps(nextProps: Props): void {
      this.maybeUpdateState(nextProps);
    }

    override componentWillUnmount(): void {
      this._unsubscribe();
      this._isMounted = false;
    }

    maybeUpdateState(props: Props): void {
      // Even though we unsubscribe from redux in unmount, there may have been a pending update to
      // this component that was queued before the unmount occurred, so we still need to do an
      // _isMounted check to be safe.
      if (!this._isMounted) {
        return;
      }
      const newState = this.computeState(props);
      if (this._options.shouldPropagateNextState(this.state, newState)) {
        this.setState(newState);
      }
    }

    computeState(props: Props): State {
      try {
        this._connector.props = props;
        return {
          isLoading: false,
          additionalProps: this._connector.enhanceProps(),
        };
      } catch (e) {
        if (e instanceof DataStillLoadingException) {
          return {
            isLoading: true,
            additionalProps: null,
          };
        }
        throw e;
      }
    }

    _handleStoreChange = (): void => {
      if (this._options.throttleUpdates) {
        this._throttledMaybeUpdateState();
      } else {
        this.maybeUpdateState(this.props);
      }
    };

    override render(): ReactNode {
      if (this.state.isLoading) {
        // Ensure that the props are in sync
        this._connector.props = this.props;
        return this._connector.renderLoading();
      } else {
        return <WrappedComponent ref={this.props.setRef} {...this.props} {...this.state.additionalProps!} />;
      }
    }
  }
  return ConnectedWrappedComponent;
}
